import { PLAN_DISPLAY_NAMES } from '@/atoms/subscription.atom';
import { userAtom } from '@/atoms/user.atom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { execute } from '@/graphql/execute';
import { BillingInterval, CollectionMethod, SubscriptionPlan } from '@/graphql/graphql';
import { usePricing } from '@/hooks/usePricing';
import { CreateSubscriptionParams, useSubscription } from '@/hooks/useSubscription';
import { validatePromoCodeQuery } from '@/queries/subscription.query';
import { useAtomValue } from 'jotai';
import { Check, CreditCard, Loader2, Tag, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PlanOption {
  plan: SubscriptionPlan;
  name: string;
  description: string;
  features: string[];
}

export const CheckoutDialog = ({ open, onOpenChange }: CheckoutDialogProps) => {
  const { createSubscription, loading, subscription } = useSubscription();
  const { getPlanPrice, formatPrice } = usePricing();
  const { t } = useTranslation('subscription');

  // TODO (later task): populate features from translations when plan-based keys are added
  const PLAN_OPTIONS: PlanOption[] = [
    { plan: 'FREE', name: t('tiers.free.name', { defaultValue: 'Free' }), description: t('tiers.free.description', { defaultValue: 'Get started for free' }), features: [] },
    { plan: 'STARTER', name: t('tiers.starter.name', { defaultValue: 'Starter' }), description: t('tiers.starter.description', { defaultValue: 'For small teams getting started' }), features: [] },
    { plan: 'TEAM', name: t('tiers.team.name', { defaultValue: 'Team' }), description: t('tiers.team.description', { defaultValue: 'For growing teams' }), features: [] },
    { plan: 'BUSINESS', name: t('tiers.business.name', { defaultValue: 'Business' }), description: t('tiers.business.description', { defaultValue: 'For larger organizations' }), features: [] },
  ];
  const user = useAtomValue(userAtom);
  const [step, setStep] = useState<'plan' | 'payment' | 'promo'>('plan');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>('TEAM' as SubscriptionPlan);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('ANNUAL' as BillingInterval);
  const [collectionMethod, setCollectionMethod] = useState<CollectionMethod>('AUTOMATIC' as CollectionMethod);
  const [promoCode, setPromoCode] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState<{
    valid: boolean;
    name: string | null;
    percentOff: number | null;
    amountOff: number | null;
  } | null>(null);

  const monthlyPrice = useMemo(
    () => getPlanPrice(selectedPlan, 'MONTHLY' as BillingInterval),
    [getPlanPrice, selectedPlan],
  );
  const annualPrice = useMemo(
    () => getPlanPrice(selectedPlan, 'ANNUAL' as BillingInterval),
    [getPlanPrice, selectedPlan],
  );
  const annualSavings = monthlyPrice * 12 - annualPrice;

  const handleNext = () => {
    if (step === 'plan') setStep('payment');
    else if (step === 'payment') setStep('promo');
  };

  const handleBack = () => {
    if (step === 'payment') setStep('plan');
    else if (step === 'promo') setStep('payment');
  };

  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleCheckout = async () => {
    setCheckoutError(null);
    try {
      const params: CreateSubscriptionParams = {
        plan: selectedPlan,
        billingInterval,
        collectionMethod,
        promoCode: promoCode || undefined,
        successUrl: `${window.location.origin}${window.location.pathname}?checkout=success`,
        cancelUrl: `${window.location.origin}${window.location.pathname}?checkout=canceled`,
        userEmail: user?.email || undefined,
      };

      const result = await createSubscription(params);

      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout. Please try again.';
      setCheckoutError(message);
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>
            {step === 'plan' && t('checkout.choosePlan')}
            {step === 'payment' && t('checkout.paymentMethod')}
            {step === 'promo' && t('checkout.reviewCheckout')}
          </DialogTitle>
          <DialogDescription>
            {step === 'plan' && t('checkout.choosePlanDescription')}
            {step === 'payment' && t('checkout.paymentMethodDescription')}
            {step === 'promo' && t('checkout.reviewDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className='py-4'>
          {/* Step 1: Plan Selection */}
          {step === 'plan' && (
            <div className='grid grid-cols-2 gap-3'>
              {PLAN_OPTIONS.map((option) => {
                const isCurrent = option.plan === subscription.plan;
                // FREE has no Stripe price — the backend rejects checkout for it,
                // so it can't be a selectable option here.
                const isDisabled = option.plan === 'FREE';
                const isSelected = selectedPlan === option.plan;

                return (
                  <div
                    key={option.plan}
                    aria-disabled={isDisabled}
                    className={`relative rounded-lg border p-3 transition-all ${
                      isDisabled
                        ? 'cursor-not-allowed opacity-60 border-slate-200 dark:border-stone-800'
                        : isSelected
                          ? 'cursor-pointer border-blue-500 ring-2 ring-blue-500'
                          : 'cursor-pointer border-slate-200 dark:border-stone-800 hover:border-slate-300'
                    }`}
                    onClick={() => {
                      if (!isDisabled) setSelectedPlan(option.plan);
                    }}>
                    <div className='flex items-start justify-between gap-2'>
                      <div>
                        <h3 className='font-semibold text-slate-900 dark:text-white text-sm'>{option.name}</h3>
                        <p className='text-xs text-slate-500 dark:text-stone-400'>{option.description}</p>
                      </div>
                      {isCurrent ? (
                        <span className='shrink-0 rounded-full bg-slate-200 dark:bg-stone-700 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:text-stone-300'>
                          {t('tierSelector.currentPlan')}
                        </span>
                      ) : isSelected ? (
                        <div className='h-4 w-4 rounded-full bg-blue-500 flex items-center justify-center shrink-0'>
                          <Check className='h-2.5 w-2.5 text-white' />
                        </div>
                      ) : null}
                    </div>
                    <ul className='mt-2 space-y-0.5'>
                      {option.features.map((feature, i) => (
                        <li key={i} className='flex items-center gap-1.5 text-xs text-slate-600 dark:text-stone-300'>
                          <Check className='h-3 w-3 text-green-500 shrink-0' />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 2: Payment Method */}
          {step === 'payment' && (
            <div className='space-y-4'>
              {/* Billing interval */}
              <div>
                <Label>{t('checkout.billingCycle')}</Label>
                <RadioGroup
                  value={billingInterval}
                  onValueChange={(v) => setBillingInterval(v as BillingInterval)}
                  className='mt-2 space-y-2'>
                  <label
                    htmlFor='annual'
                    className={`flex items-center justify-between rounded-lg border p-3 cursor-pointer transition-all ${
                      billingInterval === 'ANNUAL'
                        ? 'border-blue-500 ring-2 ring-blue-500'
                        : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                    }`}>
                    <div className='flex items-center gap-2'>
                      <RadioGroupItem value='ANNUAL' id='annual' />
                      <div>
                        <span className='font-medium'>{t('checkout.annualLabel')}</span>
                        {annualSavings > 0 && (
                          <span className='ml-2 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded'>
                            {t('checkout.savePerYear', { amount: formatPrice(annualSavings) })}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className='text-sm font-semibold text-slate-700 dark:text-stone-300'>
                      {formatPrice(annualPrice)}/yr
                    </span>
                  </label>
                  <label
                    htmlFor='monthly'
                    className={`flex items-center justify-between rounded-lg border p-3 cursor-pointer transition-all ${
                      billingInterval === 'MONTHLY'
                        ? 'border-blue-500 ring-2 ring-blue-500'
                        : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                    }`}>
                    <div className='flex items-center gap-2'>
                      <RadioGroupItem value='MONTHLY' id='monthly' />
                      <span className='font-medium'>{t('checkout.monthlyLabel')}</span>
                    </div>
                    <span className='text-sm font-semibold text-slate-700 dark:text-stone-300'>
                      {formatPrice(monthlyPrice)}/mo
                    </span>
                  </label>
                </RadioGroup>
              </div>

              {/* Payment method */}
              <div
                className={`rounded-lg border p-4 cursor-pointer transition-all ${
                  collectionMethod === 'AUTOMATIC'
                    ? 'border-blue-500 ring-2 ring-blue-500'
                    : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                }`}
                onClick={() => setCollectionMethod('AUTOMATIC' as CollectionMethod)}>
                <div className='flex items-center gap-3'>
                  <CreditCard className='h-6 w-6 text-slate-600 dark:text-stone-400' />
                  <div>
                    <h3 className='font-medium text-slate-900 dark:text-white'>{t('checkout.creditDebitCard')}</h3>
                    <p className='text-sm text-slate-500 dark:text-stone-400'>{t('checkout.automaticPayments')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Promo Code & Review */}
          {step === 'promo' && (
            <div className='space-y-6'>
              <div className='p-4 bg-slate-50 dark:bg-stone-900 rounded-lg space-y-2'>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>{t('checkout.plan')}</span>
                  <span className='font-medium'>{PLAN_DISPLAY_NAMES[selectedPlan]}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>{t('checkout.billing')}</span>
                  <span className='font-medium'>{billingInterval === 'MONTHLY' ? t('settings.monthly') : t('settings.annual')}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>{t('checkout.payment')}</span>
                  <span className='font-medium'>{collectionMethod === 'AUTOMATIC' ? t('settings.card') : t('settings.invoice')}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>{t('checkout.estimatedTotal')}</span>
                  <span className='font-semibold'>
                    {formatPrice(billingInterval === 'MONTHLY' ? monthlyPrice : annualPrice)}/
                    {billingInterval === 'MONTHLY' ? 'mo' : 'yr'}
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor='promo'>{t('checkout.promoCodeOptional')}</Label>
                {promoResult?.valid && promoCode ? (
                  <div className='mt-2 flex items-center justify-between rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20 p-3'>
                    <div className='flex items-center gap-2'>
                      <Tag className='size-4 text-green-600 dark:text-green-400' />
                      <div>
                        <p className='text-sm font-medium text-green-800 dark:text-green-200'>
                          {promoResult.name || promoCode}
                        </p>
                        <p className='text-xs text-green-600 dark:text-green-400'>
                          {promoResult.percentOff
                            ? t('checkout.percentOff', { percent: promoResult.percentOff })
                            : promoResult.amountOff
                              ? t('checkout.amountOff', { amount: formatPrice(promoResult.amountOff) })
                              : t('checkout.discountApplied')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='size-7 text-green-600 hover:text-red-500'
                      onClick={() => {
                        setPromoCode('');
                        setPromoResult(null);
                        setPromoInput('');
                      }}>
                      <X className='size-4' />
                    </Button>
                  </div>
                ) : (
                  <div className='mt-2 flex gap-2'>
                    <Input
                      id='promo'
                      placeholder={t('checkout.enterPromoCode')}
                      value={promoInput}
                      onChange={(e) => {
                        setPromoInput(e.target.value.toUpperCase());
                        if (promoResult && !promoResult.valid) setPromoResult(null);
                      }}
                      disabled={promoValidating}
                      className={promoResult && !promoResult.valid ? 'border-red-300 dark:border-red-700' : ''}
                    />
                    <Button
                      variant='outline'
                      disabled={!promoInput.trim() || promoValidating}
                      onClick={async () => {
                        setPromoValidating(true);
                        try {
                          const data = await execute(validatePromoCodeQuery, { code: promoInput.trim() });
                          const result = data.validatePromoCode;
                          setPromoResult({
                            valid: result.valid,
                            name: result.name ?? null,
                            percentOff: result.percentOff ?? null,
                            amountOff: result.amountOff ?? null,
                          });
                          if (result.valid) {
                            setPromoCode(promoInput.trim());
                          }
                        } catch {
                          setPromoResult({ valid: false, name: null, percentOff: null, amountOff: null });
                        } finally {
                          setPromoValidating(false);
                        }
                      }}>
                      {promoValidating ? <Loader2 className='size-4 animate-spin' /> : t('checkout.apply')}
                    </Button>
                  </div>
                )}
                {promoResult && !promoResult.valid && (
                  <p className='text-xs text-red-500 mt-1'>{t('checkout.invalidPromoCode')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {checkoutError && <p className='text-sm text-red-500 px-1'>{checkoutError}</p>}
        <div className='flex justify-between pt-4 border-t'>
          <Button
            variant='outline'
            onClick={step === 'plan' ? () => onOpenChange(false) : handleBack}
            disabled={loading}>
            {step === 'plan' ? t('checkout.cancel') : t('checkout.back')}
          </Button>

          {step !== 'promo' ? (
            <Button onClick={handleNext}>{t('checkout.continue')}</Button>
          ) : (
            <Button onClick={handleCheckout} disabled={loading}>
              {loading ? t('checkout.processingCheckout') : t('checkout.proceedToCheckout')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
